import { ScheduleOfValues } from 'ontraccr-common';

import { isNullOrUndefined } from '../../helpers/helpers';

export const getRowKey = ({
  projectId = null, rowId = null, templateId = null, formId = null,
}) => `${formId || projectId || templateId}.${rowId}`;

export const getRow = ({
  state,
  projectId,
  templateId,
  rowId,
  formId,
  isProgress,
}) => {
  const key = getRowKey({
    projectId,
    rowId,
    templateId,
    formId,
  });
  const stateKey = isProgress ? 'subContractUpdates' : 'scheduleOfValues';
  const {
    projects: {
      [stateKey]: {
        [key]: row = [],
      } = {},
    } = {},
  } = state;
  return row;
};

export const SOV_RESET_COLUMN_LABELS = {
  percentageComplete: '% Complete',
  progressToDate: 'Progress to Date',
  previousBillings: 'Previous Billings',
  invoiceAmount: 'Invoice',
  estimatedQuantity: 'Estimated Quantity',
  previouslyBilledQuantity: 'Previously Billed Quantity',
  billableQuantity: 'Billable Quantity',
};

export const getHistoryEntry = (row) => {
  switch (row.updatedField) {
    case 'invoiceAmount':
      return {
        field: 'Invoice Amount',
        pastValue: row.lastInvoiceAmount,
        newValue: row.invoiceAmount,
        type: 'currency',
        diff: row.invoiceAmount - row.lastInvoiceAmount,
      };
    case 'description':
      return {
        field: 'Description',
        pastValue: row.lastDescription,
        newValue: row.description,
        type: 'text',
      };
    case 'percentageComplete':
      return {
        field: 'Percentage Complete',
        pastValue: row.lastPercentageComplete,
        newValue: row.percentageComplete,
        type: 'percent',
        diff: (row.percentageComplete - row.lastPercentageComplete).toFixed(2),
      };
    case 'contractAmount':
      return {
        field: 'Contract Amount',
        pastValue: row.lastContractAmount,
        newValue: row.contractAmount,
        type: 'currency',
        diff: row.contractAmount - row.lastContractAmount,
      };
    case 'notesColumn':
      return {
        field: 'Notes',
        pastValue: row.lastNotes,
        newValue: row.notesColumn,
        type: 'text',
      };
    case 'notes':
      return {
        field: 'Notes',
        pastValue: row.lastNotes,
        newValue: row.notes,
        type: 'text',
      };
    case 'itemNumber':
      return {
        field: 'Item Number',
        pastValue: row.lastItemNumber,
        newValue: row.itemNumber,
        type: 'text',
      };
    case 'import':
      return {
        field: 'imported',
      };
    case 'number': {
      return {
        field: 'Number',
        pastValue: row.lastNumber,
        newValue: row.number,
        type: 'text',
      };
    }
    case 'section': {
      const {
        sectionName,
        lastSectionName,
        isChangeOrder,
        lastIsChangeOrder,
      } = row;

      return {
        field: 'Section',
        pastValue: lastIsChangeOrder ? 'Changes' : lastSectionName,
        newValue: isChangeOrder ? 'Changes' : sectionName,
        type: 'text',
      };
    }
    case 'estimatedQuantity': {
      return {
        field: 'Estimated Quantity',
        pastValue: row.lastEstimatedQuantity,
        newValue: row.estimatedQuantity,
        type: 'number',
      };
    }
    case 'previouslyBilledQuantity': {
      return {
        field: 'Previously Billed Quantity',
        pastValue: row.lastPreviouslyBilledQuantity,
        newValue: row.previouslyBilledQuantity,
        type: 'number',
      };
    }
    case 'billableQuantity': {
      return {
        field: 'Billable Quantity',
        pastValue: row.lastBillableQuantity,
        newValue: row.billableQuantity,
        type: 'number',
      };
    }
    case 'completedQuantity': {
      return {
        field: 'Completed Quantity',
        pastValue: row.lastCompletedQuantity,
        newValue: row.completedQuantity,
        type: 'number',
      };
    }
    case 'quantityToDate': {
      return {
        field: 'Quantity To Date',
        pastValue: row.lastQuantityToDate,
        newValue: row.quantityToDate,
        type: 'number',
      };
    }
    case 'billingCode': {
      return {
        field: 'Billing Code',
        pastValue: row.lastBillingCode,
        newValue: row.billingCode,
        type: 'text',
      };
    }
    case 'unitType': {
      return {
        field: 'Unit Type',
        pastValue: row.lastUnitType,
        newValue: row.unitType,
        type: 'text',
      };
    }
    case 'formUpdateContract': {
      return {
        field: 'Contract',
        pastValue: '',
        newValue: 'Updated from form',
        type: 'text',
      };
    }
    case 'lastSubItemDeleted': {
      return {
        field: 'Amounts',
        pastValue: '',
        newValue: 'Taken over from the last sub-item',
        type: 'text',
      };
    }
    case 'resetColumns': {
      const resetColumns = (row.resetColumns || [])
        .map((column) => SOV_RESET_COLUMN_LABELS[column] || column)
        .join(', ');
      return {
        field: 'Reset columns',
        type: 'resetColumns',
        resetColumns,
        newValue: 0,
      };
    }
    default:
      return {};
  }
};

export const isValueWithinBounds = (
  row,
  field,
  newValue,
  liveSummaryValues = [],
  isProgress = false,
) => {
  if (typeof newValue === 'string') {
    return {
      valid: true,
    };
  }
  switch (field) {
    case 'totalContractAmount':
      return {
        valid: newValue >= row.currentContractValue,
        invalidMessage: 'Total Base Contract Value is less than the current contract value',
      };
    case 'invoiceAmount':
      if (!row.oldProgressToDate) {
        return {
          valid: row.progressToDate + newValue <= row.contractAmount,
          invalidMessage: 'Invoice Amount is greater than the remaining contract amount',
        };
      }

      return {
        valid: isNullOrUndefined(row.oldProgressToDate)
          ? row.progressToDate + newValue <= row.contractAmount
          : row.oldProgressToDate + newValue <= row.contractAmount,
        invalidMessage: 'Invoice Amount is greater than the remaining contract value',
      };
    case 'contractAmount':
      if (newValue < row.progressToDate) {
        return {
          valid: false,
          invalidMessage: 'Contract Amount is less than the progress to date',
        };
      }

      if (liveSummaryValues.length && !isProgress) {
        return {
          valid: (
            newValue + liveSummaryValues[0].currentContractValue - row.contractAmount
          ) <= liveSummaryValues[0].totalContractAmount,
          invalidMessage: 'Current base contract value will exceed the total base contract value',
        };
      }
      return {
        valid: true,
      };
    default:
      return {
        valid: true,
      };
  }
};

export const isInputDirty = (row, field, newValue) => {
  switch (field) {
    case 'totalContractAmount':
      return newValue !== row.totalContractAmount;
    case 'holdbackPercentage':
      return (newValue / 100).toFixed(4) !== row.holdbackPercentage.toFixed(4);
    case 'invoiceAmount':
      return newValue !== row.oldInvoiceAmount;
    case 'contractAmount':
      return newValue !== row.oldContractAmount;
    case 'percentageComplete':
      return newValue !== row.oldPercentageComplete;
    case 'estimatedQuantity':
      return newValue !== row.oldEstimatedQuantity;
    case 'billableQuantity':
      return newValue !== row.oldBillableQuantity;
    case 'completedQuantity':
      return newValue !== row.oldCompletedQuantity;
    case 'description':
    case 'number':
    case 'unitType':
    case 'billingCode':
    case 'previouslyBilledQuantity':
      return newValue !== row[field];
    default:
      return false;
  }
};

export const getRowValue = ({
  state,
  projectId,
  templateId,
  rowId,
  formId,
  isProgress,
  submissionId,
  timestamp,
}) => {
  const row = getRow({
    state,
    projectId,
    templateId,
    rowId,
    formId,
    isProgress,
  });

  if (timestamp && submissionId) {
    // timestamp desc
    const flipped = [...row].reverse();
    return flipped.find((r) => (
      r.timestamp === timestamp || r.submissionId === submissionId
    )) || {};
  }

  // filter out edits to past bill rows
  const filteredRows = row.filter((r) => !r.submissionId);
  return filteredRows[filteredRows.length - 1] || {};
};

const itemNumberSort = (a, b) => {
  if (a.itemNumber < b.itemNumber) {
    return -1;
  }
  if (a.itemNumber > b.itemNumber) {
    return 1;
  }
  return 0;
};

export const filterScheduleOfValues = (scheduleOfValues, filterTimestamp, submissionId) => {
  const filteredScheduleOfValues = {};

  Object.keys(scheduleOfValues).forEach((row) => {
    const rowValues = scheduleOfValues[row];
    const filteredValues = [];
    rowValues.forEach((val) => {
      const { timestamp, submissionId: rowSubmissionId } = val;
      if ((submissionId && submissionId === rowSubmissionId)
        || (!rowSubmissionId && timestamp < filterTimestamp)
      ) {
        filteredValues.push(val);
      }
    });
    filteredScheduleOfValues[row] = filteredValues;
  });

  return filteredScheduleOfValues;
};

// Keys of parent rows that own sub-items, used to seed expanded tree rows.
export const getSovParentKeys = (rowGroups = []) => rowGroups.reduce((keys, rows) => (
  keys.concat(
    (rows ?? [])
      .filter((row) => row?.hasSubItems)
      .map((row) => ScheduleOfValues.getSovRowId(row)),
  )
), []);

/**
 * Once a table holds sub-items, every top level row is tinted so an item added
 * after the last sub-item does not read as part of the group above it.
 * Returns the antd rowClassName function for the rows that table renders.
 */
export const getSovRowClassName = (rows) => {
  const hasSubItems = rows?.some((row) => row?.hasSubItems);
  return (record) => (hasSubItems && record?.depth === 0 ? 'sov-parent-row' : '');
};

/**
 * antd draws the tree expand icon inside the first column, which would stack it
 * on top of the item number. Tables that have sub-items get a narrow empty
 * leading column to hold it instead; every other table is left untouched.
 */
export const getSovTreeColumns = (columns, rows, expandColumnProps) => (
  rows?.some((row) => row?.hasSubItems)
    ? [{
      key: 'sovExpand',
      dataIndex: 'sovExpand',
      title: '',
      width: 32,
      render: () => null,
      ...expandColumnProps,
    }, ...columns]
    : columns
);

/**
 * The latest logical row of every ledger key, keyed the same way, with each
 * parent that owns sub-items carrying their sum. A parent's stored numbers are
 * never trusted while it has sub-items, so rows are summed once here and every
 * caller downstream treats the parent as a normal row that totals skip.
 */
export const getSyncedSovRows = (scheduleOfValues = {}, filterPastBillEdits = true) => {
  const keys = [];
  const latestRows = [];

  Object.keys(scheduleOfValues).forEach((key) => {
    const values = (scheduleOfValues[key] ?? [])
      .filter((value) => !filterPastBillEdits || !value.submissionId);
    if (!values.length) return;
    keys.push(key);
    latestRows.push(values[values.length - 1]);
  });

  const syncedRows = ScheduleOfValues.syncSovParentRows(latestRows);
  const syncedRowMap = {};
  keys.forEach((key, index) => {
    syncedRowMap[key] = syncedRows[index];
  });

  return syncedRowMap;
};

// Sub-items are only allowed under top level base contract and custom section rows.
export const scheduleOfValuesData = ({
  projectId,
  templateId,
  totalBaseContractValue,
  holdbackPercentage,
  scheduleOfValues,
  setLogValues,
  scheduleOfValueSectionMap,
  submissionId, // attach to entries when viewing past bill
  filterPastBillEdits = true,
}) => {
  const newBaseContractValues = [];
  const newSubContractValues = [];
  const newSubContractCOs = {};
  const newChangeOrderValues = [];
  const newNonHoldbackItems = [];
  const sectionValueMap = {};
  const newLiveSummaryValue = {
    projectId,
    templateId,
    totalContractValue: 0,
    totalContractAmount: totalBaseContractValue || 0,
    totalSubContractAmount: 0,
    totalChanges: 0,
    totalBilledToDate: 0,
    totalNonHoldbackAmount: 0,
    invoiceAmount: 0,
    currentContractValue: 0,
    baseContractProgressToDate: 0,
    subContractProgressToDate: 0,
    changeOrderProgressToDate: 0,
    nonHoldbackProgressToDate: 0,
    baseContractPreviousBillings: 0,
    subContractPreviousBillings: 0,
    changeOrderPreviousBillings: 0,
    nonHoldbackPreviousBillings: 0,
    baseContractInvoiceAmount: 0,
    subContractInvoiceAmount: 0,
    changeOrderInvoiceAmount: 0,
    nonHoldbackInvoiceAmount: 0,
    holdbackAmount: 0,
    holdbackPercentage: parseFloat(holdbackPercentage || 0),
  };

  const maxItemNumbers = {
    baseContract: 1,
    subContract: 1,
    subContractCOs: 1,
    changeOrder: 1,
    nonHoldback: 1,
  };

  let mostRecentValue;
  let values;

  const syncedRows = getSyncedSovRows(scheduleOfValues, filterPastBillEdits);

  Object.keys(scheduleOfValues).forEach((key) => {
    const fullValues = scheduleOfValues[key];
    // filter out past bill edits when viewing current state of SOV
    values = fullValues.filter((value) => !filterPastBillEdits || !value.submissionId);

    if (!values.length) return;

    mostRecentValue = { ...(syncedRows[key] ?? {}) };

    if (submissionId) mostRecentValue.submissionId = submissionId;

    if ((projectId && mostRecentValue.projectId !== projectId)
      || (templateId && mostRecentValue.templateId !== templateId)
    ) {
      return;
    }

    if (mostRecentValue.rowId === 'log') {
      if (setLogValues) {
        values.sort((a, b) => b.timestamp - a.timestamp);
        setLogValues(values);
      }
      return;
    }

    const itemNumberInt = parseInt(mostRecentValue.itemNumber, 10);
    const { sectionId } = mostRecentValue;
    const relevantSection = scheduleOfValueSectionMap[sectionId];
    // The parent row now carries the sum of its sub-items, so the sub-items
    // themselves are left out of every total to avoid double counting.
    const isSubItem = ScheduleOfValues.isSubItem(mostRecentValue);

    // Live Summary Update is not a regular row
    // We just use it to update the live summary values
    if (mostRecentValue.rowId === 'liveSummaryUpdate') {
      values.forEach((value) => {
        newLiveSummaryValue.totalBilledToDate += value.previousBillings;
        newLiveSummaryValue.invoiceAmount += value.invoiceAmount;
        newLiveSummaryValue.holdbackAmount += value.holdbackAmount;
      });
      return;
    }

    if (mostRecentValue.isSubContract) {
      if (mostRecentValue.isChangeOrder) {
        if (!(mostRecentValue.parentRowId in newSubContractCOs)) {
          newSubContractCOs[mostRecentValue.parentRowId] = {
            changes: [],
            totalChanges: 0,
          };
        }
        newSubContractCOs[mostRecentValue.parentRowId].changes.push(mostRecentValue);
        newSubContractCOs[mostRecentValue.parentRowId]
          .totalChanges += mostRecentValue.contractAmount;

        newLiveSummaryValue.totalChanges += mostRecentValue.contractAmount;
        newLiveSummaryValue.changeOrderProgressToDate += mostRecentValue.progressToDate;
        newLiveSummaryValue.changeOrderPreviousBillings += mostRecentValue.previousBillings;
        newLiveSummaryValue.changeOrderInvoiceAmount += mostRecentValue.invoiceAmount;

        if (itemNumberInt >= maxItemNumbers.subContractCOs) {
          maxItemNumbers.subContractCOs = itemNumberInt + 1;
        }
      } else {
        newLiveSummaryValue.currentContractValue += mostRecentValue.contractAmount;
        newLiveSummaryValue.totalSubContractAmount += mostRecentValue.contractAmount;
        newLiveSummaryValue.subContractProgressToDate += mostRecentValue.progressToDate;
        newLiveSummaryValue.subContractPreviousBillings += mostRecentValue.previousBillings;
        newLiveSummaryValue.subContractInvoiceAmount += mostRecentValue.invoiceAmount;
        newSubContractValues.push(mostRecentValue);

        if (itemNumberInt >= maxItemNumbers.subContract) {
          maxItemNumbers.subContract = itemNumberInt + 1;
        }
      }
    } else if (mostRecentValue.isChangeOrder) {
      newLiveSummaryValue.totalChanges += mostRecentValue.contractAmount;
      newLiveSummaryValue.changeOrderProgressToDate += mostRecentValue.progressToDate;
      newLiveSummaryValue.changeOrderPreviousBillings += mostRecentValue.previousBillings;
      newLiveSummaryValue.changeOrderInvoiceAmount += mostRecentValue.invoiceAmount;
      newChangeOrderValues.push(mostRecentValue);

      if (itemNumberInt >= maxItemNumbers.changeOrder) {
        maxItemNumbers.changeOrder = itemNumberInt + 1;
      }
    } else if (mostRecentValue.excludeHoldback) {
      newLiveSummaryValue.totalNonHoldbackAmount += mostRecentValue.contractAmount;
      newLiveSummaryValue.nonHoldbackProgressToDate += mostRecentValue.progressToDate;
      newLiveSummaryValue.nonHoldbackPreviousBillings += mostRecentValue.previousBillings;
      newLiveSummaryValue.nonHoldbackInvoiceAmount += mostRecentValue.invoiceAmount;
      newNonHoldbackItems.push(mostRecentValue);

      if (itemNumberInt >= maxItemNumbers.nonHoldback) {
        maxItemNumbers.nonHoldback = itemNumberInt + 1;
      }
    } else if (mostRecentValue.sectionId) {
      if (!(sectionId in sectionValueMap)) {
        sectionValueMap[sectionId] = [];
        newLiveSummaryValue[`${sectionId}ProgressToDate`] = 0;
        newLiveSummaryValue[`${sectionId}PreviousBillings`] = 0;
        newLiveSummaryValue[`${sectionId}InvoiceAmount`] = 0;
      }

      if (!isSubItem) {
        if (relevantSection?.shouldBeInCalculations) {
          newLiveSummaryValue.currentContractValue += mostRecentValue.contractAmount;
        }

        newLiveSummaryValue[`${sectionId}ProgressToDate`] += mostRecentValue.progressToDate;
        newLiveSummaryValue[`${sectionId}PreviousBillings`] += mostRecentValue.previousBillings;
        newLiveSummaryValue[`${sectionId}InvoiceAmount`] += mostRecentValue.invoiceAmount;
      }

      sectionValueMap[sectionId].push(mostRecentValue);

      if (!isSubItem
        && (itemNumberInt >= maxItemNumbers[sectionId] || !maxItemNumbers[sectionId])
      ) {
        maxItemNumbers[sectionId] = itemNumberInt + 1;
      }
    } else {
      if (!isSubItem) {
        newLiveSummaryValue.currentContractValue += mostRecentValue.contractAmount;
        newLiveSummaryValue.baseContractProgressToDate += mostRecentValue.progressToDate;
        newLiveSummaryValue.baseContractPreviousBillings += mostRecentValue.previousBillings;
        newLiveSummaryValue.baseContractInvoiceAmount += mostRecentValue.invoiceAmount;
      }
      newBaseContractValues.push(mostRecentValue);

      if (!isSubItem && itemNumberInt >= maxItemNumbers.baseContract) {
        maxItemNumbers.baseContract = itemNumberInt + 1;
      }
    }

    if (!isSubItem && (!relevantSection || relevantSection.shouldBeInCalculations)) {
      newLiveSummaryValue.totalBilledToDate += mostRecentValue.previousBillings;
      newLiveSummaryValue.invoiceAmount += mostRecentValue.invoiceAmount;
      newLiveSummaryValue.holdbackAmount += mostRecentValue.holdbackAmount;
    }
  });

  const {
    totalContractAmount,
    totalChanges,
  } = newLiveSummaryValue;
  newLiveSummaryValue.totalContractValue = totalContractAmount + totalChanges;
  newChangeOrderValues.sort(itemNumberSort);
  newSubContractValues.sort(itemNumberSort);
  newNonHoldbackItems.sort(itemNumberSort);

  // Sub-items can only live under base contract and custom section rows.
  const baseContractTree = ScheduleOfValues.buildSovTree(newBaseContractValues);
  Object.keys(sectionValueMap).forEach((sectionId) => {
    sectionValueMap[sectionId] = ScheduleOfValues.buildSovTree(sectionValueMap[sectionId]);
  });

  return {
    newBaseContractValues: baseContractTree,
    newSubContractValues,
    newSubContractCOs,
    newChangeOrderValues,
    newNonHoldbackItems,
    newLiveSummaryValue,
    maxItemNumbers,
    sectionValueMap,
  };
};

export const getNumberText = ({
  isChangeOrder,
  isSubContract,
  itemNumber,
  subItemLabel,
} = {}) => {
  if (!itemNumber && !subItemLabel) return '';
  let prefix = '';
  if (isSubContract) prefix += 'S';
  if (isChangeOrder) prefix += 'CO';
  return `${prefix}${subItemLabel || itemNumber}`;
};

export const getSubContractsWithTotalChanges = ({
  subContractValues = [],
  subContractCOs = {},
}) => (
  subContractValues.map((subcontract) => {
    const {
      [subcontract.rowId]: {
        totalChanges = 0,
      } = {},
    } = subContractCOs;
    return { ...subcontract, totalChanges };
  })
);

export const getSubContractsWithTotalChangesMap = ({
  projectIds,
  subContractMap,
  subContractCOMap,
}) => {
  if (!projectIds.length) return {};

  let allProjectSubContracts = [];

  projectIds.forEach((pId) => {
    const projectSubContracts = subContractMap?.[pId] ?? [];
    allProjectSubContracts = [...allProjectSubContracts, ...projectSubContracts];
  });

  return getSubContractsWithTotalChanges({
    subContractValues: allProjectSubContracts,
    subContractCOs: subContractCOMap,
  });
};
